import { Component, type SyntheticEvent } from "react";
import { type FormCreateProps } from "../../utils/form-create-props";
import { type VehicleType, type VehicleTypeCreate } from "../../api";
import { action, computed, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import { createUuid } from "../../utils/uuid";
import { pick } from "ramda";
import { DefaultButton, Stack, TextField } from "@fluentui/react";
import { doubleBindString } from "../../utils/double-bind";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import sizes from "../sizes.scss";
import { store } from "../../store";

export interface FormCreateVehicleTypeProps extends FormCreateProps<VehicleType> {}

@observer
export class FormCreateVehicleType extends Component<FormCreateVehicleTypeProps> {
    @observable public accessor label = "";

    private validationId = createUuid();

    constructor(props: FormCreateVehicleTypeProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryVehicleTypes.validation.create.updateModel(
            this.validationId,
            this.siteCreate,
        );
    }

    componentDidMount(): void {
        store.repositoryVehicleTypes.validation.create.initializeModel(
            this.validationId,
            this.siteCreate,
        );
    }

    @computed private get siteCreate(): VehicleTypeCreate {
        return pick(["label"], this as FormCreateVehicleType);
    }

    @action.bound
    private async createVehicleType(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const site = await store.repositoryVehicleTypes.create(this.siteCreate);
        this.reset();
        if (this.props.onCreate) {
            this.props.onCreate(site);
        }
    }

    @action.bound private reset(): void {
        this.label = "";
        this.triggerValidation();
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formCreateVehicleType.submit.text")}
                validation={store.repositoryVehicleTypes.validation.create}
                validationId={this.validationId}
            />
        );

        return (
            <form onSubmit={this.createVehicleType}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t("formCreateVehicleType.label.label")}
                        {...doubleBindString<FormCreateVehicleType>(this, "label", () =>
                            this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicleTypes.validation.create.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formCreateVehicleType.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
